import { AgoraManager } from './AgoraManager';
import AgoraRTC from 'agora-rtc-sdk-ng';

export const EVENTS = {
  USER_JOINED: 'user-joined',
  USER_LEFT: 'user-left',
  USER_PUBLISHED: 'user-published',
  USER_UNPUBLISHED: 'user-unpublished',
  INFO_UPDATE: 'user-info-updated',
  NETWORK_QUALITY: 'network-quality',
  CONNECTION_STATE: 'connection-state-change',
};

const REMOTE_PLAYER_ID = 'remote-player-container';
const LOCAL_PLAYER_ID = 'local-player-container';

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1));

class VideoCallService {
  _remotePlayerContainer = null;
  _videoOn = true;
  _audioOn = true;
  _blurOn = false;
  _cameraFacing = 'user';
  _hasRemoteUser = false; // Flag para controlar se já existe usuário remoto
  _channelParameters = {
    localAudioTrack: null,
    localVideoTrack: null,
    remoteAudioTrack: null,
    remoteVideoTrack: null,
    remoteUid: null
  };
  _events = {};

  constructor() {
    this._agoraManager = new AgoraManager(this._eventHandler.bind(this));
  }

  async generateUid(uid) {
    const random = Math.floor(10000000000 + Math.random() * 90000000000).toString();
    if (!uid) {
      // Gerar 11 números aleatórios se UID não for fornecido
      return random;
    }
    // Remover todos os caracteres especiais, mantendo apenas números e letras
    const uidTmp = uid.replace(/[^a-zA-Z0-9]/g, '').trim();
    
    // Se após limpar ficou vazio, gerar 11 números aleatórios
    if (!uidTmp) {
      return random;
    }
    
    return uidTmp;
  }

  async _eventHandler(eventName, input) {
    if (eventName === EVENTS.USER_PUBLISHED) {
      await this._onRemoteUserPublished(input);
    }

    if (eventName === EVENTS.USER_LEFT) {
      const remotes = this._agoraManager.getRemoteUsers() || [];
      this._hasRemoteUser = remotes.length > 0;

      if (remotes.length === 0) {
        this.clearRemotePlayback();
      } else {
        const leftUid = input.user?.uid?.toString();
        if (leftUid && leftUid === this._channelParameters.remoteUid) {
          this._channelParameters.remoteUid = null;
          this._channelParameters.remoteVideoTrack = null;
          this._channelParameters.remoteAudioTrack = null;
        }
      }
    }

    if (eventName === EVENTS.USER_UNPUBLISHED) {
      const remotes = this._agoraManager.getRemoteUsers() || [];
      if (remotes.length === 0) {
        this.clearRemotePlayback();
      }
    }

    this._emit(eventName, input);
  }

  async _onRemoteUserPublished({ user, mediaType }) {
    if (mediaType === 'audio') {
      this._channelParameters.remoteAudioTrack = user.audioTrack;
      if (user.audioTrack) {
        user.audioTrack.play();
      }
      return;
    }

    if (mediaType !== 'video' || !user?.videoTrack) {
      return;
    }

    this._channelParameters.remoteVideoTrack = user.videoTrack;
    this._channelParameters.remoteAudioTrack = user.audioTrack;
    this._channelParameters.remoteUid = user.uid.toString();
    this._hasRemoteUser = true;

    await this._playRemoteVideo(user.videoTrack);
  }

  async _playRemoteVideo(videoTrack) {
    const expandRoot = document.getElementById('expand_stream');
    if (!expandRoot || !videoTrack) {
      return;
    }

    this._createRemotePlayerContainer();
    expandRoot.innerHTML = '';
    expandRoot.appendChild(this._remotePlayerContainer);

    await videoTrack.play(this._remotePlayerContainer, { fit: 'cover' });
    this._ensureVideoElementsPlaying(this._remotePlayerContainer, {
      muted: false
    });
  }

  async ensureRemotePlayback() {
    const remotes = this._agoraManager.getRemoteUsers() || [];
    for (const user of remotes) {
      try {
        if (user.hasAudio && !user.audioTrack) {
          await this._agoraManager.subscribeRemote(user, 'audio');
        }
        if (user.hasVideo && !user.videoTrack) {
          await this._agoraManager.subscribeRemote(user, 'video');
        }

        if (user.audioTrack) {
          this._channelParameters.remoteAudioTrack = user.audioTrack;
          user.audioTrack.play();
        }
        if (user.videoTrack) {
          this._channelParameters.remoteVideoTrack = user.videoTrack;
          this._channelParameters.remoteUid = user.uid.toString();
          this._hasRemoteUser = true;
          await this._playRemoteVideo(user.videoTrack);
        }
      } catch (_error) {}
    }
  }

  /**
   * Remove player remoto e limpa flags (participante remoto saiu / caiu).
   */
  clearRemotePlayback() {
    this._hasRemoteUser = false;
    this._channelParameters.remoteUid = null;
    this._channelParameters.remoteVideoTrack = null;
    this._channelParameters.remoteAudioTrack = null;
    this._removeView(REMOTE_PLAYER_ID);
    const expandRoot = document.getElementById('expand_stream');
    if (expandRoot) {
      expandRoot.innerHTML = '';
    }
  }

  _createRemotePlayerContainer() {
    this._removeView(REMOTE_PLAYER_ID);
    const div = document.createElement('div');
    div.id = REMOTE_PLAYER_ID;
    div.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:hidden;background:#111;';
    this._remotePlayerContainer = div;
  }

  _appendRemoteVideoContainer() {
    const expandRoot = document.getElementById('expand_stream');
    if (!expandRoot || !this._remotePlayerContainer) return;
    expandRoot.appendChild(this._remotePlayerContainer);
  }

  async startLocalPreview() {
    const localRoot = this._getLocalStream();
    if (!localRoot) return false;

    if (!this._channelParameters.localVideoTrack) {
      try {
        this._channelParameters.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      } catch (_error) {}

      try {
        this._channelParameters.localVideoTrack = await this._agoraManager._createCameraTrack();
      } catch {
        return false;
      }
      this._detectCameraFacing();
    }

    return this.ensureLocalPreview();
  }

  _detectCameraFacing() {
    const track = this._channelParameters.localVideoTrack;
    if (!track) return;
    this._cameraFacing = this._agoraManager.detectCameraFacing(track) || 'user';
  }

  async ensureLocalPreview() {
    const localRoot = this._getLocalStream();
    const track = this._channelParameters.localVideoTrack;

    if (!localRoot || !track) return false;

    try {
      localRoot.innerHTML = '';
      localRoot.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:hidden;background:#111;';

      await track.play(localRoot, {
        fit: 'cover',
        mirror: this._cameraFacing !== 'environment',
      });

      this._ensureVideoElementsPlaying(localRoot, { muted: true });
      return localRoot.querySelector('video') != null;
    } catch {
      return false;
    }
  }

  hasLocalVideoTrack() {
    return Boolean(this._channelParameters.localVideoTrack);
  }

  _ensureVideoElementsPlaying(root, { muted = true } = {}) {
    if (!root) return;
    const videos = root.querySelectorAll('video');
    videos.forEach((video) => {
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.autoplay = true;
      video.muted = muted;
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  async joinChannel({ channel, token, uid }) {
    const joinParameters = {
      channelName: channel,
      token: token,
      uid: uid
    };
    this._removeView(REMOTE_PLAYER_ID);

    const hadVideoTrack = Boolean(this._channelParameters.localVideoTrack);
    const joinId = await this._agoraManager.join(
      this._getLocalStream(),
      null,
      this._channelParameters,
      joinParameters
    );
    if (!hadVideoTrack) {
      this._detectCameraFacing();
    }
    return {
      joinId: joinId,
      channelParameters: this._channelParameters,
      joinParameters: joinParameters
    };
  }

  _getLocalStream() {
    return document.getElementById('local_stream');
  }

  _createLocalPlayerContainer() {
    this._removeView(LOCAL_PLAYER_ID);
    const div = document.createElement('div');
    div.setAttribute('id', LOCAL_PLAYER_ID);
    return div;
  }

  async leaveRoom() {
    if (this._channelParameters.localAudioTrack || this._channelParameters.localVideoTrack) {
      try {
        await this._agoraManager.leave(this._channelParameters);
      } catch {}
    }
    this._removeView(LOCAL_PLAYER_ID);
    this._removeView(REMOTE_PLAYER_ID);

    this._hasRemoteUser = false;
    this._cameraFacing = 'user';
    this._channelParameters.remoteUid = null;
    this._channelParameters.localAudioTrack = null;
    this._channelParameters.localVideoTrack = null;
    this._channelParameters.remoteAudioTrack = null;
    this._channelParameters.remoteVideoTrack = null;
  }

  _removeView(id) {
    if (!id) return;
    const div = document.getElementById(id);
    if (div) {
      div.remove();
    }
  }

  async toggleVideo(toggle) {
    const newVideoStatus = toggle;
    await this._channelParameters.localVideoTrack.setMuted(!newVideoStatus);
    this._videoOn = newVideoStatus;
    return this._videoOn;
  }

  async toggleAudio(toggle) {
    const newAudioStatus = toggle;
    if (!this._channelParameters.localAudioTrack) {
      this._audioOn = newAudioStatus;
      return this._audioOn;
    }
    await this._channelParameters.localAudioTrack.setMuted(!newAudioStatus);
    this._audioOn = newAudioStatus;
    return this._audioOn;
  }

  async switchCamera() {
    const track = this._channelParameters.localVideoTrack;
    if (!track) return this._cameraFacing;

    const nextFacing = this._cameraFacing === 'environment' ? 'user' : 'environment';

    try {
      this._cameraFacing = await this._agoraManager.switchCamera(track, nextFacing);
    } finally {
      await this._restoreBlurAfterCameraChange();
      await this.ensureLocalPreview();
    }
    return this._cameraFacing;
  }

  async _restoreBlurAfterCameraChange() {
    if (!this._blurOn) return;
    try {
      await this._agoraManager.restoreVirtualBackground(this._channelParameters);
    } catch {
      this._blurOn = false;
      try {
        await this._agoraManager.disableBackground(this._channelParameters);
      } catch {}
    }
  }

  isBlurOn() {
    return this._blurOn;
  }

  async canSwitchCamera() {
    if (isIOS()) return true;
    return (await this._agoraManager.getCameraCount()) > 1;
  }

  onCameraChanged(listener) {
    AgoraRTC.onCameraChanged = listener || null;
  }

  async toggleBlur(toggle) {
    const newBlurStatus = toggle;
    if (newBlurStatus) {
      await this._agoraManager.setBackgroundBlurring(this._channelParameters);
    } else {
      await this._agoraManager.disableBackground(this._channelParameters);
    }
    this._blurOn = newBlurStatus;
    return this._blurOn;
  }

  on(eventName, listener) {
    if (!this._events[eventName]) {
      this._events[eventName] = [];
    }
    this._events[eventName].push(listener);
  }

  cleanEvents() {
    this._events = {};
  }

  getRemoteUsers() {
    return this._agoraManager.getRemoteUsers();
  }

  hasRemoteUser() {
    return (this._hasRemoteUser || (this._agoraManager.getRemoteUsers()?.length ?? 0) > 0);
  }

  getConnectionState() {
    return this._agoraManager.getConnectionState?.() || 'DISCONNECTED';
  }

  _emit(eventName, input = {}) {
    if (!this._events[eventName]) return;
    this._events[eventName].forEach((listener) => {
      listener(input);
    });
  }
}

export default new VideoCallService();
